import React, { useState, createContext, useContext, ReactNode } from 'react';
import { classNames } from '@/utils/classNames';

interface TabsContextValue {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  variant: 'default' | 'pills' | 'underline';
  size: 'sm' | 'md' | 'lg';
}

const TabsContext = createContext<TabsContextValue | undefined>(undefined);

const useTabsContext = () => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tab components must be used within a Tabs component');
  }
  return context;
};

interface TabsProps {
  defaultTab?: string;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  variant?: 'default' | 'pills' | 'underline';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  children: ReactNode;
}

interface TabListProps {
  className?: string;
  children: ReactNode;
}

interface TabProps {
  id: string;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
}

interface TabPanelsProps {
  className?: string;
  children: ReactNode;
}

interface TabPanelProps {
  id: string;
  className?: string;
  children: ReactNode;
}

const Tabs: React.FC<TabsProps> & {
  List: React.FC<TabListProps>;
  Tab: React.FC<TabProps>;
  Panels: React.FC<TabPanelsProps>;
  Panel: React.FC<TabPanelProps>;
} = ({
  defaultTab,
  activeTab: controlledActiveTab,
  onTabChange,
  variant = 'default',
  size = 'md',
  className,
  children,
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState(defaultTab || '');

  const activeTab = controlledActiveTab !== undefined ? controlledActiveTab : internalActiveTab;

  const setActiveTab = (tab: string) => {
    if (controlledActiveTab === undefined) {
      setInternalActiveTab(tab);
    }
    onTabChange?.(tab);
  };

  const contextValue: TabsContextValue = {
    activeTab,
    setActiveTab,
    variant,
    size,
  };

  return (
    <TabsContext.Provider value={contextValue}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
};

const TabList: React.FC<TabListProps> = ({ className, children }) => {
  const { variant } = useTabsContext();

  const variantClasses = {
    default: 'border-b border-gray-200 dark:border-gray-700',
    pills: 'bg-gray-100 dark:bg-gray-800 rounded-lg p-1',
    underline: 'border-b border-gray-200 dark:border-gray-700',
  };

  return (
    <div
      className={classNames(
        'flex',
        variantClasses[variant],
        variant === 'pills' && 'space-x-1',
        className
      )}
      role='tablist'
    >
      {children}
    </div>
  );
};

const Tab: React.FC<TabProps> = ({ id, disabled, className, children }) => {
  const { activeTab, setActiveTab, variant, size } = useTabsContext();

  const isActive = activeTab === id;

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  };

  const variantClasses = {
    default: {
      base: 'border-b-2 font-medium transition-colors touch-target',
      active: 'border-primary-500 text-primary-600 dark:text-primary-400',
      inactive:
        'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600',
    },
    pills: {
      base: 'rounded-md font-medium transition-colors touch-target',
      active: 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 shadow-sm',
      inactive: 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300',
    },
    underline: {
      base: 'border-b-2 font-medium transition-colors touch-target relative',
      active: 'border-primary-500 text-primary-600 dark:text-primary-400',
      inactive:
        'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300',
    },
  };

  const baseClasses = classNames(
    variantClasses[variant].base,
    sizeClasses[size],
    isActive ? variantClasses[variant].active : variantClasses[variant].inactive,
    disabled && 'opacity-50 cursor-not-allowed',
    !disabled && 'cursor-pointer',
    className
  );

  return (
    <button
      type='button'
      role='tab'
      aria-selected={isActive}
      aria-controls={`panel-${id}`}
      id={`tab-${id}`}
      disabled={disabled}
      className={baseClasses}
      onClick={() => !disabled && setActiveTab(id)}
    >
      {children}
    </button>
  );
};

const TabPanels: React.FC<TabPanelsProps> = ({ className, children }) => {
  return <div className={className}>{children}</div>;
};

const TabPanel: React.FC<TabPanelProps> = ({ id, className, children }) => {
  const { activeTab } = useTabsContext();

  if (activeTab !== id) {
    return null;
  }

  return (
    <div
      role='tabpanel'
      id={`panel-${id}`}
      aria-labelledby={`tab-${id}`}
      className={classNames('fade-in', className)}
    >
      {children}
    </div>
  );
};

Tabs.List = TabList;
Tabs.Tab = Tab;
Tabs.Panels = TabPanels;
Tabs.Panel = TabPanel;

export default Tabs;
